import React, { FC } from 'react'
import { useParams } from 'react-router-dom'
import { useStatus } from '@sketch-hq/sketch-web-renderer'

import { RouteParams, routes, IS_EMBEDDED } from '@sketch/constants'
import { ShareWithoutVersion } from 'modules/versioning'

import { DocumentSidebarLayoutHeaderProps } from 'containers/DocumentSidebarLayout'
import { ModalRestrictor } from 'containers/ModalManager'

import Navbar from 'components/Navbar'
import {
  useForDesktop,
  useForTablet,
  IsEmbedded,
  NotEmbedded,
} from '@sketch/components'
import { Zoom } from 'components/Zoom'

import { usePlaceDraftAnnotation } from 'modules/annotations/hooks'

import HeaderBadge from '../../../components/HeaderBadge'
import HeaderNavigation from '../../../components/HeaderNavigation'
import HeaderSidebarToggle from '../../../components/HeaderSidebarToggle'
import {
  EditInSketchButton,
  OpenTemplateButton,
} from '../../../components/EditInSketchButton'
import VersionLinkButton from '../../../components/VersionLinkButton'
import DocumentNavbar from '../../../components/DocumentNavbar'
import BackButton, { useBackButton } from '../../../components/BackButton'
import DocumentSettingsDropdown from '../../../components/DocumentHeader/DocumentSettingsDropdown'
import { MAX_ZOOM, MIN_ZOOM } from '../../constants'
import usePageZoom from '../../hooks/usePageZoom'

import {
  MiddleContainer,
  NavbarBackButtonItem,
  NavbarItem,
  ActionsNavbarItem,
  AddAnnotationText,
} from './PageCanvasViewHeader.styles'
import { useFlag } from 'containers/flags'

import { AddAnnotationNavbarItem } from 'modules/annotations/components'
import SketchHomeButton from 'components/SketchHomeButton'

type PageCanvasViewHeaderProps = {
  share: ShareWithoutVersion
  isViewingLatestVersion: boolean
  userCanOpenInApp: boolean
} & Pick<
  DocumentSidebarLayoutHeaderProps,
  'setSidebarLeftOpen' | 'toggleSidebarRight' | 'isSidebarRightOpen'
>

export const PageCanvasViewHeader: FC<PageCanvasViewHeaderProps> = ({
  share,
  setSidebarLeftOpen,
  toggleSidebarRight,
  isSidebarRightOpen,
  isViewingLatestVersion,
  userCanOpenInApp,
}) => {
  const { workspace, type } = share
  const badge = workspace.userIsMember && !IS_EMBEDDED ? 'workspace' : 'home'

  const isSetAsTemplateFFEnabled = useFlag('set-as-template')
  const { pageUUID } = useParams<RouteParams<'SHARE_PAGE_CANVAS_VIEW'>>()
  const isDesktop = useForDesktop()
  const isTabletAndBigger = useForTablet()
  const canOpenInApp = userCanOpenInApp && isDesktop && !IS_EMBEDDED
  const showLatestVersionButton =
    isTabletAndBigger && !isViewingLatestVersion && !IS_EMBEDDED

  const [zoom, setZoom] = usePageZoom()
  const [placeDraftAnnotation] = usePlaceDraftAnnotation() || []
  const { link: shouldRenderBackButton } = useBackButton()
  const canvasStatus = useStatus()
  const isTemplate = type === 'TEMPLATE'

  if (placeDraftAnnotation && isTabletAndBigger && !IS_EMBEDDED) {
    return (
      <Navbar>
        <MiddleContainer />
        <AddAnnotationText>Click anywhere to comment</AddAnnotationText>
        <MiddleContainer>
          <NavbarItem>
            <Zoom
              zoom={zoom}
              setZoom={setZoom}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
            />
          </NavbarItem>
          <AddAnnotationNavbarItem disabled={canvasStatus?.type !== 'READY'} />
        </MiddleContainer>
        <HeaderSidebarToggle
          toggleSidebar={toggleSidebarRight}
          isSidebarOpen={isSidebarRightOpen}
        />
      </Navbar>
    )
  }

  return (
    <Navbar>
      <NotEmbedded>
        <Navbar.Section>
          <HeaderBadge
            type={badge}
            workspace={workspace}
            setSidebarLeftOpen={setSidebarLeftOpen}
          />

          {isTabletAndBigger && shouldRenderBackButton && (
            <NavbarBackButtonItem>
              <BackButton />
            </NavbarBackButtonItem>
          )}

          <HeaderNavigation share={share} />
        </Navbar.Section>

        <Navbar.FixedWidthSection align="end">
          {isTabletAndBigger && (
            <>
              <NavbarItem>
                <DocumentNavbar />
              </NavbarItem>
              <NavbarItem>
                <Zoom
                  zoom={zoom}
                  setZoom={setZoom}
                  minZoom={MIN_ZOOM}
                  maxZoom={MAX_ZOOM}
                />
              </NavbarItem>
            </>
          )}

          {canOpenInApp && isTemplate && isSetAsTemplateFFEnabled && (
            <NavbarItem>
              <OpenTemplateButton
                shareId={share.identifier}
                disabled={!isViewingLatestVersion}
              />
            </NavbarItem>
          )}

          {canOpenInApp && !isTemplate && (
            <NavbarItem>
              <EditInSketchButton
                shareId={share.identifier}
                disabled={!isViewingLatestVersion}
              />
            </NavbarItem>
          )}

          {showLatestVersionButton && (
            <NavbarItem>
              <VersionLinkButton
                to={routes.SHARE_PAGE_CANVAS_VIEW.create({
                  shareID: share.identifier,
                  pageUUID: pageUUID,
                })}
              >
                See Latest Version
              </VersionLinkButton>
            </NavbarItem>
          )}

          <ActionsNavbarItem>
            {isTabletAndBigger && (
              <AddAnnotationNavbarItem
                disabled={canvasStatus?.type !== 'READY'}
              />
            )}
            <ModalRestrictor>
              <DocumentSettingsDropdown />
            </ModalRestrictor>
          </ActionsNavbarItem>

          <HeaderSidebarToggle
            toggleSidebar={toggleSidebarRight}
            isSidebarOpen={isSidebarRightOpen}
          />
        </Navbar.FixedWidthSection>
      </NotEmbedded>

      <IsEmbedded>
        <Navbar.Section>
          <SketchHomeButton />

          {isTabletAndBigger && (
            <NavbarBackButtonItem>
              <BackButton />
            </NavbarBackButtonItem>
          )}

          <HeaderNavigation share={share} />
        </Navbar.Section>

        <Navbar.FixedWidthSection align="end">
          <NavbarItem>
            <Zoom
              zoom={zoom}
              setZoom={setZoom}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
            />
          </NavbarItem>
        </Navbar.FixedWidthSection>
      </IsEmbedded>
    </Navbar>
  )
}

export default PageCanvasViewHeader
